import { AlertCircle, ArrowRight, ImagePlus, LoaderCircle, LockKeyhole, Trash2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../app/AppContext';
import { Atmosphere } from '../components/Atmosphere';
import { PhaseLabel } from '../components/PhaseLabel';
import { analytics } from '../services/analytics';
import {
  disposePreparedImages,
  MAX_IMAGES,
  prepareImage,
  type PreparedImage,
} from '../services/images';
import { getStyleAnalyzer, manualAnalysisResult } from '../services/style-analysis';

export function DiscoverPage() {
  const navigate = useNavigate();
  const { profile, setStyleProfile, setStyleReferencePreviews } = useApp();
  const [images, setImages] = useState<PreparedImage[]>([]);
  const [context, setContext] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const imagesRef = useRef(images);
  imagesRef.current = images;

  useEffect(() => () => disposePreparedImages(imagesRef.current), []);

  const addFiles = async (files: FileList | null) => {
    if (!files) return;
    setError('');
    const available = MAX_IMAGES - images.length;
    if (files.length > available) setError(`You can add ${available} more image${available === 1 ? '' : 's'}.`);
    for (const file of Array.from(files).slice(0, available)) {
      try {
        const prepared = await prepareImage(file);
        setImages((current) => [...current, prepared]);
      } catch (caught) {
        setError(caught instanceof Error ? caught.message : 'The image could not be prepared.');
      }
    }
  };

  const removeImage = (id: string) => {
    setImages((current) => {
      const target = current.find((image) => image.id === id);
      if (target) URL.revokeObjectURL(target.previewUrl);
      return current.filter((image) => image.id !== id);
    });
  };

  const analyze = async () => {
    if (!images.length) return setError('Add at least one image to begin.');
    setLoading(true);
    setError('');
    analytics.track('images_submitted', { count: images.length });
    try {
      const result = await getStyleAnalyzer().analyzeImages({ images, context });
      setStyleProfile(result.profile);
      setStyleReferencePreviews(images.map((image) => image.previewUrl));
      imagesRef.current = [];
      setImages([]);
      navigate('/style');
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Analysis is unavailable.');
    } finally {
      setLoading(false);
    }
  };

  const continueManually = () => {
    setStyleProfile(manualAnalysisResult().profile);
    setStyleReferencePreviews(images.map((image) => image.previewUrl));
    imagesRef.current = [];
    setImages([]);
    navigate('/style');
  };

  return (
    <div className="discover-page page-pad">
      <Atmosphere variant="both" />
      <header className="page-intro narrow">
        <PhaseLabel index="Phase 01">Input / Visual DNA</PhaseLabel>
        <h1>Build a visual<br /><i>reference set.</i></h1>
        <p>Choose clothes, interiors, objects, textures or artwork. We analyse visible aesthetic properties—not people.</p>
      </header>

      <section className="upload-workspace" aria-labelledby="upload-heading">
        {profile.migrationNotice && <div className="migration-notice" role="status"><strong>Style Card update</strong><p>{profile.migrationNotice}</p></div>}
        <label className="upload-zone" htmlFor="image-input">
          <ImagePlus size={30} />
          <h2 id="upload-heading">Upload inspiration</h2>
          <p>One to five JPEG, PNG or WebP images. Maximum 8 MB each.</p>
          <span className="button button-primary">Select files</span>
          <input id="image-input" data-testid="image-input" type="file" accept="image/jpeg,image/png,image/webp" multiple onChange={(event) => void addFiles(event.target.files)} />
        </label>

        {images.length > 0 && (
          <div className="image-tray" aria-label={`${images.length} prepared images`}>
            {images.map((image, index) => (
              <figure key={image.id}>
                <img src={image.previewUrl} alt={`Reference preview ${index + 1}`} />
                <figcaption><span>{String(index + 1).padStart(2, '0')} / Ready</span><button type="button" onClick={() => removeImage(image.id)} aria-label={`Remove ${image.name}`}><Trash2 size={15} /></button></figcaption>
              </figure>
            ))}
          </div>
        )}

        <label className="field full-field">
          <span>Optional context</span>
          <textarea value={context} maxLength={1000} onChange={(event) => setContext(event.target.value)} placeholder="What draws you to these references? Avoid personal or sensitive information." />
        </label>

        <div className="privacy-callout"><LockKeyhole size={20} /><div><strong>Your images are temporary</strong><p>STYLE re-encodes them for analysis and does not retain the source files. Only your resulting Style Card may be saved locally. No facial recognition or demographic analysis.</p></div></div>
        {error && <div className="error-banner" role="alert"><AlertCircle size={18} />{error}</div>}
        <div className="form-actions">
          <button className="button button-primary button-large" type="button" onClick={() => void analyze()} disabled={loading}>
            {loading ? <><LoaderCircle className="spin" /> Analysing visible signals</> : <>Analyse references <ArrowRight /></>}
          </button>
          <button className="text-button" type="button" onClick={continueManually}>Continue with a manual Style Card</button>
        </div>
      </section>
    </div>
  );
}
